import {
    AvailabilityStatus,
    Listing,
    ListingFreshness,
    ListingFreshnessStatus,
    OptionAvailability,
    PriceSnapshot,
    ProductCluster,
    SourceStatus
} from './domain';
import {
    getPublicPriceBreakdown,
    normalizeListingFreshness,
    PriceBreakdownChip,
    PublicPriceBreakdown
} from './price-utils';

export type LowestOfferSourceType = 'listing' | 'price_snapshot';
export type LowestOfferChipKind = 'status' | 'shipping' | 'coupon' | 'checked_at' | 'stock' | 'freshness';
export type LowestOfferChipTone = 'default' | 'accent' | 'warning' | 'danger' | 'muted';
export type LowestOfferExclusionReason =
    | 'sold_out'
    | 'restock_expected'
    | 'stock_checking'
    | 'all_options_sold_out'
    | 'freshness_expired'
    | 'freshness_failed'
    | 'api_pending';

export interface LowestOfferPolicy {
    comparableFreshnessStatuses: ListingFreshnessStatus[];
    comparableStockStatuses: AvailabilityStatus[];
    excludedSourceStatuses: SourceStatus[];
}

export interface LowestOfferChip {
    id: string;
    label: string;
    kind: LowestOfferChipKind;
    tone: LowestOfferChipTone;
}

export interface LowestOfferCandidate {
    id: string;
    listingId: string;
    productClusterId: string;
    sourceType: LowestOfferSourceType;
    listing: Listing | null;
    snapshot: PriceSnapshot | null;
    effectivePrice: number;
    publicEffectivePrice: number;
    listedPrice: number;
    checkedAt: string | null;
    collectedAt: string | null;
    sourceStatus: SourceStatus;
    availabilityStatus: AvailabilityStatus;
    freshness: ListingFreshness | null;
    breakdown: PublicPriceBreakdown;
    priceBreakdownChips: PriceBreakdownChip[];
    optionAvailability: OptionAvailability[];
    excludedFromLowest: boolean;
    exclusionReasons: LowestOfferExclusionReason[];
    chips: LowestOfferChip[];
}

export interface LowestOfferInput {
    productCluster?: ProductCluster | null;
    productClusterId?: string | null;
    listings?: Listing[];
    priceSnapshots?: PriceSnapshot[];
    now?: Date;
    policy?: Partial<LowestOfferPolicy>;
}

export interface LowestOfferResult {
    productClusterId: string | null;
    lowestCandidate: LowestOfferCandidate | null;
    candidates: LowestOfferCandidate[];
    comparableCandidates: LowestOfferCandidate[];
    excludedCandidates: LowestOfferCandidate[];
    evidenceChips: LowestOfferChip[];
    policy: LowestOfferPolicy;
}

export const DEFAULT_LOWEST_OFFER_POLICY: LowestOfferPolicy = {
    comparableFreshnessStatuses: ['fresh', 'stale'],
    comparableStockStatuses: ['in_stock', 'low_stock'],
    excludedSourceStatuses: ['api_pending']
};

function timestamp(value: string | null): number {
    if (!value) return NaN;
    const parsed = new Date(value).getTime();
    return Number.isFinite(parsed) ? parsed : NaN;
}

function formatCheckedAt(value: string): string {
    const matched = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
    if (!matched) return value;
    return `${matched[2]}.${matched[3]} ${matched[4]}:${matched[5]}`;
}

function mergePolicy(policy?: Partial<LowestOfferPolicy>): LowestOfferPolicy {
    return {
        comparableFreshnessStatuses: policy?.comparableFreshnessStatuses || DEFAULT_LOWEST_OFFER_POLICY.comparableFreshnessStatuses,
        comparableStockStatuses: policy?.comparableStockStatuses || DEFAULT_LOWEST_OFFER_POLICY.comparableStockStatuses,
        excludedSourceStatuses: policy?.excludedSourceStatuses || DEFAULT_LOWEST_OFFER_POLICY.excludedSourceStatuses
    };
}

function uniqueReasons(reasons: LowestOfferExclusionReason[]): LowestOfferExclusionReason[] {
    return Array.from(new Set(reasons));
}

function uniqueChips(chips: LowestOfferChip[]): LowestOfferChip[] {
    const seen = new Set<string>();
    const result: LowestOfferChip[] = [];

    chips.forEach((chip) => {
        const key = chip.id || chip.label;
        if (seen.has(key)) return;
        seen.add(key);
        result.push(chip);
    });

    return result;
}

function optionAvailabilityFromListing(listing: Listing): OptionAvailability[] {
    return (listing.options || []).map((option) => ({
        listing_option_id: option.id,
        color: option.color,
        size: option.size,
        availability_status: option.availability_status,
        stock_count: option.stock_count,
        restock_expected_at: option.restock_expected_at,
        restock_alert_available: option.restock_alert_available,
        checked_at: option.checked_at || option.last_checked_at
    }));
}

function isOptionPurchasable(option: OptionAvailability, policy: LowestOfferPolicy): boolean {
    return policy.comparableStockStatuses.includes(option.availability_status);
}

function sourceStatusFromFreshness(freshness: ListingFreshness | null): SourceStatus | null {
    return freshness?.sourceStatus || freshness?.source_status || null;
}

function freshnessForListing(listing: Listing, now: Date): ListingFreshness {
    return listing.freshness || normalizeListingFreshness({
        checkedAt: listing.checked_at || listing.last_checked_at,
        collectedAt: listing.collected_at || listing.checked_at || listing.last_checked_at,
        confidenceScore: listing.confidence ?? 0.5,
        sourceStatus: listing.source_status
    }, now);
}

function freshnessForSnapshot(snapshot: PriceSnapshot, now: Date): ListingFreshness {
    return snapshot.freshness || snapshot.listing_freshness || normalizeListingFreshness({
        checkedAt: snapshot.checkedAt || snapshot.checked_at,
        collectedAt: snapshot.collectedAt || snapshot.collected_at,
        sourceStatus: snapshot.sourceStatus || snapshot.source_status
    }, now);
}

function exclusionReasons(input: {
    availabilityStatus: AvailabilityStatus;
    freshness: ListingFreshness | null;
    sourceStatus: SourceStatus;
    optionAvailability: OptionAvailability[];
    policy: LowestOfferPolicy;
}): LowestOfferExclusionReason[] {
    const reasons: LowestOfferExclusionReason[] = [];

    if (input.availabilityStatus === 'sold_out') reasons.push('sold_out');
    if (input.availabilityStatus === 'restock_expected') reasons.push('restock_expected');
    if (input.availabilityStatus === 'checking') reasons.push('stock_checking');
    if (!input.policy.comparableStockStatuses.includes(input.availabilityStatus) && reasons.length === 0) {
        reasons.push('stock_checking');
    }

    if (
        input.optionAvailability.length > 0 &&
        !input.optionAvailability.some((option) => isOptionPurchasable(option, input.policy))
    ) {
        reasons.push('all_options_sold_out');
    }

    if (input.freshness?.status === 'expired') reasons.push('freshness_expired');
    if (input.freshness?.status === 'failed') reasons.push('freshness_failed');
    if (input.freshness?.status === 'api_pending') reasons.push('api_pending');
    if (
        input.freshness &&
        !input.policy.comparableFreshnessStatuses.includes(input.freshness.status) &&
        !['expired', 'failed', 'api_pending'].includes(input.freshness.status)
    ) {
        reasons.push('api_pending');
    }

    if (
        input.policy.excludedSourceStatuses.includes(input.sourceStatus) ||
        sourceStatusFromFreshness(input.freshness) === 'api_pending'
    ) {
        reasons.push('api_pending');
    }

    return uniqueReasons(reasons);
}

function freshnessRank(status: ListingFreshnessStatus | null): number {
    if (status === 'fresh') return 0;
    if (status === 'stale') return 1;
    if (status === 'expired') return 2;
    if (status === 'failed') return 3;
    if (status === 'api_pending') return 4;
    return 5;
}

function stockChip(input: {
    availabilityStatus: AvailabilityStatus;
    optionAvailability: OptionAvailability[];
}): LowestOfferChip | null {
    const options = input.optionAvailability;
    if (input.availabilityStatus === 'sold_out') {
        return { id: 'stock-sold-out', label: '품절', kind: 'stock', tone: 'danger' };
    }
    if (input.availabilityStatus === 'restock_expected') {
        return { id: 'stock-restock-expected', label: '재입고 예정', kind: 'stock', tone: 'warning' };
    }
    if (input.availabilityStatus === 'checking') {
        return { id: 'stock-checking', label: '재고 확인 필요', kind: 'stock', tone: 'warning' };
    }

    if (options.length > 0) {
        const soldOutCount = options.filter((option) => option.availability_status === 'sold_out').length;
        const lowStockCount = options.filter((option) => option.availability_status === 'low_stock').length;

        if (soldOutCount === options.length) {
            return { id: 'stock-all-sold-out', label: '전체 품절', kind: 'stock', tone: 'danger' };
        }
        if (soldOutCount > 0) {
            return { id: 'stock-partial-sold-out', label: '일부 사이즈 품절', kind: 'stock', tone: 'warning' };
        }
        if (lowStockCount > 0 || input.availabilityStatus === 'low_stock') {
            return { id: 'stock-low', label: '일부 사이즈 재고 적음', kind: 'stock', tone: 'warning' };
        }
    }

    if (input.availabilityStatus === 'low_stock') {
        return { id: 'stock-low', label: '재고 적음', kind: 'stock', tone: 'warning' };
    }

    return null;
}

function freshnessChip(freshness: ListingFreshness | null): LowestOfferChip {
    if (!freshness) {
        return { id: 'freshness-unknown', label: '확인 필요', kind: 'freshness', tone: 'warning' };
    }

    if (freshness.status === 'fresh') {
        return { id: 'freshness-fresh', label: '최근 확인', kind: 'freshness', tone: 'accent' };
    }
    if (freshness.status === 'stale') {
        return { id: 'freshness-stale', label: '확인 필요', kind: 'freshness', tone: 'warning' };
    }
    if (freshness.status === 'expired') {
        return { id: 'freshness-expired', label: '재확인 필요', kind: 'freshness', tone: 'warning' };
    }
    if (freshness.status === 'failed') {
        return { id: 'freshness-failed', label: '확인 실패', kind: 'freshness', tone: 'danger' };
    }

    return { id: 'freshness-api-pending', label: '연동 대기', kind: 'freshness', tone: 'danger' };
}

function createEvidenceChips(candidate: LowestOfferCandidate, isLowest: boolean): LowestOfferChip[] {
    const chips: LowestOfferChip[] = [];
    if (isLowest) chips.push({ id: 'current-lowest', label: '현재 최저', kind: 'status', tone: 'accent' });

    if (candidate.breakdown.unknownComponents.includes('shipping_fee')) {
        chips.push({ id: 'shipping-needs-check', label: '배송비 확인 필요', kind: 'shipping', tone: 'warning' });
    } else {
        chips.push({ id: 'shipping-included', label: '배송비 포함', kind: 'shipping', tone: 'accent' });
    }

    if (candidate.breakdown.publicCoupon > 0) {
        chips.push({ id: 'public-coupon-applied', label: '공개 쿠폰 적용', kind: 'coupon', tone: 'accent' });
    } else if (candidate.breakdown.unknownComponents.includes('public_coupon')) {
        chips.push({ id: 'public-coupon-needs-check', label: '쿠폰 확인 필요', kind: 'coupon', tone: 'warning' });
    }

    if (candidate.checkedAt) {
        chips.push({
            id: 'checked-at',
            label: `확인 ${formatCheckedAt(candidate.checkedAt)}`,
            kind: 'checked_at',
            tone: 'muted'
        });
    } else {
        chips.push({ id: 'checked-at-missing', label: '확인 시각 없음', kind: 'checked_at', tone: 'warning' });
    }

    const stock = stockChip({
        availabilityStatus: candidate.availabilityStatus,
        optionAvailability: candidate.optionAvailability
    });
    if (stock) chips.push(stock);

    chips.push(freshnessChip(candidate.freshness));

    if (candidate.excludedFromLowest) {
        chips.push({ id: 'excluded-from-lowest', label: '최저가 제외', kind: 'status', tone: 'warning' });
    }

    return uniqueChips(chips);
}

function createListingCandidate(listing: Listing, policy: LowestOfferPolicy, now: Date): LowestOfferCandidate {
    const freshness = freshnessForListing(listing, now);
    const breakdown = (listing as Listing & { price_breakdown?: PublicPriceBreakdown }).price_breakdown || getPublicPriceBreakdown(listing);
    const optionAvailability = optionAvailabilityFromListing(listing);
    const reasons = exclusionReasons({
        availabilityStatus: listing.availability_status,
        freshness,
        sourceStatus: listing.source_status,
        optionAvailability,
        policy
    });

    return {
        id: listing.id,
        listingId: listing.id,
        productClusterId: listing.product_cluster_id,
        sourceType: 'listing',
        listing,
        snapshot: null,
        effectivePrice: breakdown.public_effective_price,
        publicEffectivePrice: breakdown.public_effective_price,
        listedPrice: breakdown.basePrice,
        checkedAt: freshness.checkedAt || listing.checked_at || listing.last_checked_at || null,
        collectedAt: freshness.collectedAt || listing.collected_at || null,
        sourceStatus: listing.source_status,
        availabilityStatus: listing.availability_status,
        freshness,
        breakdown,
        priceBreakdownChips: breakdown.chips,
        optionAvailability,
        excludedFromLowest: reasons.length > 0,
        exclusionReasons: reasons,
        chips: []
    };
}

function createSnapshotCandidate(snapshot: PriceSnapshot, policy: LowestOfferPolicy, now: Date): LowestOfferCandidate {
    const freshness = freshnessForSnapshot(snapshot, now);
    const breakdown = getPublicPriceBreakdown(snapshot);
    const availabilityStatus = snapshot.stockStatus || snapshot.availability_status;
    const sourceStatus = snapshot.sourceStatus || snapshot.source_status;
    const optionAvailability = snapshot.optionAvailability || snapshot.option_availability || [];
    const reasons = exclusionReasons({
        availabilityStatus,
        freshness,
        sourceStatus,
        optionAvailability,
        policy
    });

    return {
        id: snapshot.id,
        listingId: snapshot.listingId || snapshot.listing_id,
        productClusterId: snapshot.productClusterId || snapshot.product_cluster_id,
        sourceType: 'price_snapshot',
        listing: null,
        snapshot,
        effectivePrice: breakdown.public_effective_price,
        publicEffectivePrice: breakdown.public_effective_price,
        listedPrice: breakdown.basePrice,
        checkedAt: snapshot.checkedAt || snapshot.checked_at || freshness.checkedAt || null,
        collectedAt: snapshot.collectedAt || snapshot.collected_at || freshness.collectedAt || null,
        sourceStatus,
        availabilityStatus,
        freshness,
        breakdown,
        priceBreakdownChips: breakdown.chips,
        optionAvailability,
        excludedFromLowest: reasons.length > 0,
        exclusionReasons: reasons,
        chips: []
    };
}

function shouldReplaceCandidate(current: LowestOfferCandidate, next: LowestOfferCandidate): boolean {
    const currentTime = timestamp(current.checkedAt);
    const nextTime = timestamp(next.checkedAt);
    if (Number.isFinite(currentTime) && Number.isFinite(nextTime) && nextTime !== currentTime) {
        return nextTime > currentTime;
    }
    if (!Number.isFinite(currentTime) && Number.isFinite(nextTime)) return true;
    if (current.sourceType !== next.sourceType) return next.sourceType === 'listing';
    return false;
}

function sortByLowest(a: LowestOfferCandidate, b: LowestOfferCandidate): number {
    const priceDiff = a.effectivePrice - b.effectivePrice;
    if (priceDiff !== 0) return priceDiff;

    const freshnessDiff = freshnessRank(a.freshness?.status || null) - freshnessRank(b.freshness?.status || null);
    if (freshnessDiff !== 0) return freshnessDiff;

    const recentDiff = timestamp(b.checkedAt) - timestamp(a.checkedAt);
    if (Number.isFinite(recentDiff) && recentDiff !== 0) return recentDiff;

    if (a.sourceType !== b.sourceType) return a.sourceType === 'listing' ? -1 : 1;
    return a.id.localeCompare(b.id);
}

function resolveProductClusterId(input: LowestOfferInput): string | null {
    return input.productCluster?.id || input.productClusterId || null;
}

export function evaluateLowestOffer(input: LowestOfferInput = {}): LowestOfferResult {
    const policy = mergePolicy(input.policy);
    const now = input.now || new Date();
    const productClusterId = resolveProductClusterId(input);
    const byListingId = new Map<string, LowestOfferCandidate>();

    (input.listings || []).forEach((listing) => {
        if (productClusterId && listing.product_cluster_id !== productClusterId) return;
        const candidate = createListingCandidate(listing, policy, now);
        byListingId.set(candidate.listingId, candidate);
    });

    (input.priceSnapshots || []).forEach((snapshot) => {
        const snapshotClusterId = snapshot.productClusterId || snapshot.product_cluster_id;
        if (productClusterId && snapshotClusterId !== productClusterId) return;
        const candidate = createSnapshotCandidate(snapshot, policy, now);
        const current = byListingId.get(candidate.listingId);
        if (!current || shouldReplaceCandidate(current, candidate)) {
            byListingId.set(candidate.listingId, candidate);
        }
    });

    const baseCandidates = Array.from(byListingId.values());
    const comparableCandidates = baseCandidates
        .filter((candidate) => !candidate.excludedFromLowest)
        .sort(sortByLowest);
    const lowestCandidateBase = comparableCandidates[0] || null;
    const lowestKey = lowestCandidateBase ? `${lowestCandidateBase.sourceType}:${lowestCandidateBase.id}` : '';
    const candidates = baseCandidates.map((candidate) => ({
        ...candidate,
        chips: createEvidenceChips(candidate, `${candidate.sourceType}:${candidate.id}` === lowestKey)
    }));
    const lowestCandidate = lowestCandidateBase
        ? candidates.find((candidate) => `${candidate.sourceType}:${candidate.id}` === lowestKey) || null
        : null;
    const excludedCandidates = candidates.filter((candidate) => candidate.excludedFromLowest);

    return {
        productClusterId,
        lowestCandidate,
        candidates,
        comparableCandidates: candidates.filter((candidate) => !candidate.excludedFromLowest).sort(sortByLowest),
        excludedCandidates,
        evidenceChips: lowestCandidate?.chips || [],
        policy
    };
}

export function evaluateListings(
    listings: Listing[],
    priceSnapshots: PriceSnapshot[] = [],
    productCluster?: ProductCluster | null,
    now?: Date
): LowestOfferResult {
    return evaluateLowestOffer({
        productCluster,
        productClusterId: productCluster?.id || listings[0]?.product_cluster_id || priceSnapshots[0]?.product_cluster_id || null,
        listings,
        priceSnapshots,
        now
    });
}

export const lowestOfferService = {
    evaluateLowestOffer,
    evaluateListings
};
